import { useCallback, useState } from 'react'

const DEFAULT_INITIAL_COUNT = 5
const DEFAULT_PAGE_SIZE = 10

type UseMobilePagedStudyListParams<T> = {
  items: T[]
  isMobile: boolean
  hasServerMore?: boolean
  loadMore?: () => Promise<boolean>
  initialCount?: number
  pageSize?: number
}

type UseMobilePagedStudyListResult<T> = {
  visibleItems: T[]
  hasMobileMore: boolean
  shouldShowMobileButton: boolean
  handleMobileToggle: () => Promise<void>
  resetMobilePage: () => void
}

export const useMobilePagedStudyList = <T>({
  items,
  isMobile,
  hasServerMore = false,
  loadMore,
  initialCount = DEFAULT_INITIAL_COUNT,
  pageSize = DEFAULT_PAGE_SIZE,
}: UseMobilePagedStudyListParams<T>): UseMobilePagedStudyListResult<T> => {
  const [page, setPage] = useState<number>(1)
  const totalCount = items.length
  const visibleCount = (page - 1) * pageSize + initialCount
  const visibleItems = isMobile ? items.slice(0, visibleCount) : items
  const totalPageCount = Math.ceil(Math.max(0, totalCount - initialCount) / pageSize) + 1
  const hasMobileMore = hasServerMore ? totalPageCount >= page : totalPageCount > page
  const shouldShowMobileButton = isMobile && totalCount >= initialCount

  const resetMobilePage = useCallback((): void => {
    setPage(1)
  }, [])

  const handleMobileToggle = useCallback(async (): Promise<void> => {
    if (!hasMobileMore) {
      resetMobilePage()
      return
    }
    const shouldLoadMore = hasServerMore && totalCount - visibleItems.length < pageSize
    if (shouldLoadMore) {
      if (!loadMore) return
      const isLoaded = await loadMore()
      if (!isLoaded) return
    }
    setPage((prevPage) => prevPage + 1)
  }, [
    hasMobileMore,
    hasServerMore,
    loadMore,
    pageSize,
    resetMobilePage,
    totalCount,
    visibleItems.length,
  ])

  return {
    visibleItems,
    hasMobileMore,
    shouldShowMobileButton,
    handleMobileToggle,
    resetMobilePage,
  }
}
